import axios from 'axios'
import { useSelector } from 'react-redux'
import { useState, useCallback, useRef, useEffect } from 'react'

// util: detect webview2 host
const isWebView2 = () => typeof window !== 'undefined' && !!(window.chrome && window.chrome.webview)

// maps an incoming uploadmanager item (or file) to a stable key we can match with the file
const keyOf = (fileOrItem) => {
  // prefer explicit relativepath if present, otherwise fall back to common fields
  return (
    fileOrItem?.relativePath ||
    fileOrItem?.webkitRelativePath ||
    fileOrItem?.name ||
    fileOrItem?.fileName
  )
}

const useFileUpload = (onUploadsComplete) => {
  const maxChunkSize = useSelector((state) => state.app.maxChunkSize)
  const CHUNK_SIZE = Math.max(1, Number(maxChunkSize || 1024)) * 1024 // bytes

  const [uploadProgress, setUploadProgress] = useState({})
  const [isUploading, setIsUploading] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  const abortControllerRef = useRef(null)
  const pauseRef = useRef(false)

  // host batch map: key -> { fileUrl, fileName, fileSize, token, batchId, objectId }
  const hostItemsRef = useRef(new Map())
  // pending resolvers for batches waiting on host items
  const pendingBatchesRef = useRef(new Map())

  // ===== webview2 message wiring =====
  useEffect(() => {
    if (!isWebView2()) return

    const onMessage = (evt) => {
      const msg = evt?.data || {}
      if (msg?.channel !== 'host') return

      if (msg?.type === 'upload-start-batch' && Array.isArray(msg.items)) {
        // build lookup by multiple keys so matching is resilient
        const m = hostItemsRef.current
        msg.items.forEach((it) => {
          const k1 = it?.relativePath || it?.webkitRelativePath
          const k2 = it?.fileName || it?.name
          const k3 = keyOf(it)
          if (k1) m.set(k1, it)
          if (k2) m.set(k2, it)
          if (k3) m.set(k3, it)
        })

        // resolve any waiter for this batch
        if (msg?.batchId && pendingBatchesRef.current.has(msg.batchId)) {
          pendingBatchesRef.current.get(msg.batchId)()
          pendingBatchesRef.current.delete(msg.batchId)
        }
      }

      if (msg?.type === 'upload-error') {
        // surface host-reported errors to console
        console.error('[uploadmanager] upload-error:', msg?.message)
      }
    }

    window.chrome.webview.addEventListener('message', onMessage)
    return () => {
      try {
        window.chrome.webview.removeEventListener('message', onMessage)
      } catch {}
    }
  }, [])

  // wait for the host to respond with items for this batch (with timeout)
  const waitForHostBatch = useCallback((batchId, timeoutMs = 2500) => {
    if (!isWebView2()) return Promise.resolve()

    // if we already saw any host items, proceed without delay; the map is global across batches
    if (hostItemsRef.current.size > 0) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      let done = false
      const finish = () => {
        if (!done) {
          done = true
          resolve()
        }
      }
      pendingBatchesRef.current.set(batchId, finish)

      // timeout so we can gracefully fall back to browser file blobs
      setTimeout(() => {
        if (pendingBatchesRef.current.get(batchId)) {
          pendingBatchesRef.current.delete(batchId)
          finish()
        }
      }, timeoutMs)
    })
  }, [])

  // post a request to the host for a batch if we're in webview2
  const requestHostBatch = useCallback((files, batchId) => {
    if (!isWebView2()) return

    const items = files.map((f) => ({
      // desktop app may attach localpath/path on the file; use if present
      localPath: f.localPath || f.path || '',
      fileName: f.name,
      relativePath: keyOf(f),
      batchId,
      objectId: f.objectId || ''
    }))

    try {
      window.chrome.webview.postMessage({
        channel: 'host',
        type: 'request-upload-batch',
        items
      })
    } catch (e) {
      console.warn('failed to post request-upload-batch', e)
    }
  }, [])

  // ===== low-level chunk upload =====
  const updateProgress = (relativePath, chunkIndex, totalChunks) => {
    const totalProgress = Math.round(((chunkIndex + 1) / Math.max(1, totalChunks)) * 100)
    setUploadProgress((prev) => ({
      ...prev,
      [relativePath]: { totalProgress }
    }))
  }

  const postChunk = async (batchId, fileMeta, chunkBlob, chunkIndex, totalChunks, signal) => {
    const formData = new FormData()
    formData.append('fileChunk', chunkBlob)
    formData.append('chunkIndex', chunkIndex)
    formData.append('totalChunks', totalChunks)
    formData.append('batchId', batchId)
    formData.append('fileName', fileMeta.name)
    formData.append('objectId', fileMeta.objectId)
    formData.append('relativePath', fileMeta.relativePath)

    return axios.post('/api/wt/documents/uploadfile', formData, { signal })
  }

  const fetchRangeFromHost = async (fileUrl, start, end, signal) => {
    // end is exclusive in our math; convert to inclusive for range header
    const lastByte = end - 1
    const res = await fetch(fileUrl, {
      method: 'GET',
      headers: { Range: `bytes=${start}-${lastByte}` },
      credentials: 'include',
      signal
    })

    if (!res.ok && res.status !== 206 && res.status !== 200) {
      throw new Error(`host range fetch failed: ${res.status}`)
    }
    const buf = await res.arrayBuffer()
    return new Blob([buf])
  }

  const uploadFile = async (batchId, file, signal) => {
    const relativePath = keyOf(file)
    const { name, objectId = '' } = file

    // decide source: browser file blob vs host-provided url
    const hostItem =
      hostItemsRef.current.get(relativePath) ||
      hostItemsRef.current.get(name) ||
      hostItemsRef.current.get(file?.webkitRelativePath)

    // build a unified size
    const size = hostItem?.fileSize ?? file.size ?? 0

    // zero-byte shortcut
    if (!size || size === 0) {
      await postChunk(batchId, { name, relativePath, objectId }, new Blob(), 0, 1, signal)
      return
    }

    const totalChunks = Math.ceil(size / CHUNK_SIZE)

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      // simple cooperative pause
      // eslint-disable-next-line no-await-in-loop
      while (pauseRef.current) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 100))
      }

      const start = chunkIndex * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, size)

      let chunkBlob
      if (hostItem?.fileUrl) {
        // read from host via http range
        // eslint-disable-next-line no-await-in-loop
        chunkBlob = await fetchRangeFromHost(hostItem.fileUrl, start, end, signal)
      } else {
        // normal browser slice
        // eslint-disable-next-line no-await-in-loop
        chunkBlob = file.slice(start, end)
      }

      // eslint-disable-next-line no-await-in-loop
      await postChunk(
        batchId,
        { name, relativePath, objectId },
        chunkBlob,
        chunkIndex,
        totalChunks,
        signal
      )

      updateProgress(relativePath, chunkIndex, totalChunks)
    }

    // tell host we're done with this token (do not delete the original file unless you want that behavior)
    if (hostItem?.token && isWebView2()) {
      try {
        window.chrome.webview.postMessage({
          channel: 'host',
          type: 'host-delete-local',
          token: hostItem.token,
          deleteFile: false
        })
      } catch {}
    }
  }

  const uploadFiles = useCallback(
    async (batch) => {
      setIsUploading(true)
      pauseRef.current = false
      abortControllerRef.current = new AbortController()

      const { files, batchId } = batch
      const { signal } = abortControllerRef.current
      abortControllerRef.current.batchId = batchId

      try {
        // prime host mapping if available and wait briefly for response
        if (isWebView2()) {
          requestHostBatch(files, batchId)
          // avoid race: ensure hostitemsref is populated (or timeout to fall back)
          await waitForHostBatch(batchId)
        }

        for (let i = 0; i < files.length; i++) {
          // eslint-disable-next-line no-await-in-loop
          await uploadFile(batchId, files[i], signal)
        }

        if (onUploadsComplete) onUploadsComplete({ batchId: batch.batchId, ...batch?.meta })
      } catch (error) {
        if (axios.isCancel?.(error)) {
          console.log('upload cancelled')
        } else if (error?.name === 'AbortError') {
          console.log('upload aborted')
        } else {
          console.error('error during file upload:', error)
        }
      } finally {
        setIsUploading(false)
      }
    },
    [onUploadsComplete, requestHostBatch, waitForHostBatch] // include deps so react can memoize correctly
  )
  const pauseUpload = useCallback(() => {
    setIsPaused(true)
    pauseRef.current = true
  }, [])

  const resumeUpload = useCallback(() => {
    setIsPaused(false)
    pauseRef.current = false
  }, [])

  const cancelCleanup = useCallback((batchId) => {
    axios.post('/api/wt/documents/cancelbatch', { batchId })
  }, [])

  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      cancelCleanup(abortControllerRef.current.batchId)
      abortControllerRef.current = null
    }
  }, [cancelCleanup])


  return {
    uploadFiles,
    cancelUpload,
    pauseUpload,
    resumeUpload,
    uploadProgress,
    isUploading,
    isPaused,
    cancelCleanup
  }
}

export default useFileUpload
